import { lazy, Suspense, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TicketsRail from '../../components/tickets/TicketsRail';
import TicketsList from '../../components/tickets/TicketsList';
import TicketDetail from '../../components/tickets/TicketDetail';
import TicketForm from '../../components/tickets/TicketForm';
import TicketsRespuestas from '../../components/tickets/TicketsRespuestas';
import TicketsBloqueos from '../../components/tickets/TicketsBloqueos';
import {
  TICKETS_HEADQUARTER,
  VISTAS,
  ticketsKeys,
  useGrupos,
  useTicketsUsuarios,
} from '../../features/tickets';
import { getItem, STORAGE_KEYS } from '../../services/storage';
import './Tickets.scss';

// Informes arrastra react-apexcharts (~140 kB). Se carga en diferido para no
// meterlo en el bundle principal: pantalla limitada a SUPERADMIN y SECRETARIA.
const TicketsInformes = lazy(() => import('../../components/tickets/TicketsInformes'));

const NAV_INICIAL = { type: 'view', val: 'todos' };

// Segmentos reservados de /tickets/:seccion. Cualquier otro valor no numerico
// se considera invalido y redirige a la lista.
const PANTALLAS_FIJAS = {
  nuevo: 'new',
  informes: 'informes',
  respuestas: 'respuestas',
  bloqueos: 'bloqueos',
};

// Estado de navegacion (pantalla + id) derivado de la URL, para que el detalle
// de un ticket sea enlazable/compartible.
const navegacionDesdeUrl = (seccion) => {
  if (!seccion) return { screen: 'list', currentId: null };
  if (PANTALLAS_FIJAS[seccion]) return { screen: PANTALLAS_FIJAS[seccion], currentId: null };
  if (/^\d+$/.test(seccion)) return { screen: 'detail', currentId: seccion };
  return null;
};

// Titulo de la cabecera segun la navegacion activa.
const tituloNav = (nav, grupos) => {
  if (nav.type === 'grupo') {
    const grupo = grupos.find((g) => String(g.id) === String(nav.val));
    return grupo ? grupo.nombre : 'Tickets';
  }
  const vista = VISTAS.find((v) => v.id === nav.val);
  return vista ? vista.label : 'Tickets';
};

const Tickets = () => {
  // Punto unico de la sede (preparado para un futuro selector).
  const headquarter = TICKETS_HEADQUARTER;

  const { seccion } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const urlNav = navegacionDesdeUrl(seccion);

  // Bandeja/vista activa tambien en la URL (?bandeja= / ?vista=) para que la
  // lista filtrada sobreviva a un refresco o a compartir el enlace.
  const nav = useMemo(() => {
    if (seccion === 'informes') return { type: 'informe', val: 'actividad' };
    if (seccion === 'respuestas') return { type: 'respuestas', val: 'gestion' };
    if (seccion === 'bloqueos') return { type: 'bloqueos', val: 'gestion' };
    const bandeja = searchParams.get('bandeja');
    if (bandeja) return { type: 'grupo', val: bandeja };
    const vista = searchParams.get('vista');
    if (vista && VISTAS.some((v) => v.id === vista)) return { type: 'view', val: vista };
    return NAV_INICIAL;
  }, [seccion, searchParams]);

  const [refresco, setRefresco] = useState(0);

  const { data: usuarios = [] } = useTicketsUsuarios(headquarter);
  const { data: grupos = [] } = useGrupos(headquarter);

  // Agente del usuario logueado (para la vista "Mis tickets"): match por email.
  const agenteActualId = useMemo(() => {
    const email = getItem(STORAGE_KEYS.USER)?.profileObj?.email?.toLowerCase();
    if (!email) return null;
    const yo = usuarios.find((u) => (u.email || '').toLowerCase() === email);
    return yo ? yo.id : null;
  }, [usuarios]);

  // Filtros derivados de la navegacion (bandeja/vista); el resto los pone la propia tabla.
  const filtrosNav = useMemo(() => {
    if (nav.type === 'grupo') return { grupo: nav.val };
    const vista = VISTAS.find((v) => v.id === nav.val);
    const f = vista?.filtros || {};
    return {
      ...(f.estado ? { estado: f.estado } : {}),
      ...(f.sin_asignar ? { sinAsignar: true } : {}),
      ...(nav.val === 'mias' && agenteActualId ? { agente: agenteActualId } : {}),
    };
  }, [nav, agenteActualId]);

  // Refresca la tabla y el resto del modulo (catalogos, usuarios) sin recargar la pestana.
  const queryClient = useQueryClient();
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ticketsKeys.all });
    setRefresco((n) => n + 1);
  };

  const seleccionarNav = (nuevo) => {
    if (nuevo.type === 'informe') navigate('/tickets/informes');
    else if (nuevo.type === 'respuestas') navigate('/tickets/respuestas');
    else if (nuevo.type === 'bloqueos') navigate('/tickets/bloqueos');
    else if (nuevo.type === 'grupo') navigate(`/tickets?bandeja=${nuevo.val}`);
    else navigate(nuevo.val === 'todos' ? '/tickets' : `/tickets?vista=${nuevo.val}`);
  };

  // Conserva la query (bandeja/vista) para que "volver" restaure la lista filtrada.
  const abrirTicket = (id) => navigate(`/tickets/${id}${location.search}`);

  const volverALista = () => navigate(`/tickets${location.search}`);

  // Segmento no reconocido (ni pantalla fija ni id numerico): a la lista.
  if (!urlNav) return <Navigate to="/tickets" replace />;
  const { screen, currentId } = urlNav;

  const rail = <TicketsRail grupos={grupos} nav={nav} onSelect={seleccionarNav} />;

  return (
    <div className="tickets-view theme-new">
      {/* En la lista, la barra de bandejas y vistas se pliega con el botón «Filtros» de la tabla. */}
      {screen !== 'list' && rail}

      {screen === 'list' && (
        <TicketsList
          panelSuperior={rail}
          titulo={tituloNav(nav, grupos)}
          filtros={filtrosNav}
          refresco={refresco}
          onOpen={abrirTicket}
          onNew={() => navigate('/tickets/nuevo')}
          onRefresh={refrescar}
        />
      )}

      {screen === 'detail' && currentId && (
        <TicketDetail headquarter={headquarter} id={currentId} onBack={volverALista} />
      )}

      {screen === 'new' && (
        <TicketForm
          headquarter={headquarter}
          onCancel={volverALista}
          // replace: que "atras" desde el detalle no vuelva al formulario ya enviado
          onCreated={(id) => navigate(`/tickets/${id}`, { replace: true })}
        />
      )}

      {screen === 'informes' && (
        <Suspense fallback={<div className="tickets-panel tickets-empty">Cargando informes…</div>}>
          <TicketsInformes headquarter={headquarter} />
        </Suspense>
      )}

      {screen === 'respuestas' && <TicketsRespuestas headquarter={headquarter} />}

      {screen === 'bloqueos' && <TicketsBloqueos headquarter={headquarter} />}
    </div>
  );
};

export default Tickets;
